import formStyles from '../formStyles.module.scss';
import textInputStyles from './textInput.module.scss';

/**
 * Generic input field with a label. Defaults to a text input but supports other input types.
 *
 * @remarks
 * This is a controlled input. The value is managed via the `value` prop and updated with `onChange`.
 * The `onChange` callback provides the current input value and the native change event for flexibility. You can then use the value to update your form state as needed or run any other function onchange.
 * Additional native `<input>` props (e.g. `placeholder`, `disabled`, `autoComplete`) can be passed via `...rest`.
 *
 * If you provide `helpText` or `errorMessage`, they are programmatically associated via `aria-describedby`.
 * Providing an `errorMessage` marks the field invalid (`aria-invalid`) and renders the error text.
 * When `showRequired` is false, the input remains accessible via `aria-required` but the visual indicator is hidden.
 *
 * @param props.id - Unique input ID used to associate the label
 * @param props.name - Field name used for form submission/validation
 * @param props.title - Visible label text
 * @param props.type Optional - Input type (default: 'text')
 * @param props.placeholder Optional - Placeholder text for the input
 * @param props.value - Current value of the input
 * @param props.onChange Optional - (value, e?) => {someFunc(value)}. Value is the current input value, e is the native change event if you need it. SomeFunc is the function you want to call with the new value.
 * @param props.onBlur Optional - (value, e?) => {someFunc(value)}. Value is the current input value, e is the native blur event if you need it. SomeFunc is the function you want to call with the new value on blur.
 * @param props.required Optional - Marks the field required (default: false)
 * @param props.showRequired Optional - Controls whether the required asterisk (*) is shown
 * @param props.helpText Optional - Helper text shown under the label
 * @param props.errorMessage Optional - Error message shown under the input
 * @param props.disabled Optional - Disables the input
 * @param props.displayStyle Optional - Hides the field visually (honeypot for spam prevention)
 * @param props.formInstructionsId Optional - ID for additional accessibility description
 * @param props.rest - Any additional native `<input>` attributes
 *
 * @example
 * ```tsx
 * <TextInput
 *   id="email"
 *   name="email"
 *   title="Email"
 *   type="email"
 *   value={form.email}
 *   onChange={(emailValue) => setForm({ ...form, email: emailValue })}
 *   placeholder="Enter your email"
 * />
 * ```
 */
const TextInput = ({
    id,
    name,
    title,
    type = 'text',
    placeholder = '',
    value,
    onChange,
    onBlur,
    required = false,
    showRequired = true,
    helpText,
    errorMessage,
    disabled,
    hideOffScreen = false,
    formInstructionsId,
    ...rest
}: {
    id: string;
    name: string;
    title: string;
    type?: React.HTMLInputTypeAttribute;
    placeholder?: string;
    value: string;
    onChange?: (value: string, e?: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (value: string, e?: React.FocusEvent<HTMLInputElement>) => void;
    required?: boolean;
    showRequired?: boolean;
    helpText?: string;
    errorMessage?: string;
    disabled?: boolean;
    hideOffScreen?: boolean;
    formInstructionsId?: string;
} & Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'onBlur' | 'value' | 'type'
>) => {
    const helpId = `${id}-help`;
    const errorId = `${id}-error`;
    const asterisk = (
        <span
            aria-hidden={!showRequired}
            className={formStyles.requiredAsterisk}
        >
            {' '}
            *
        </span>
    );
    const describedBy = [
        helpText ? helpId : null,
        errorMessage ? errorId : null,
        formInstructionsId || null,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div
            data-testid="textinput"
            className={`${formStyles.inputcontainer} ${hideOffScreen && textInputStyles.displayStyle}`}
            aria-hidden={hideOffScreen || undefined}
        >
            <label
                data-testid="textinputlabel"
                htmlFor={id}
                className={`${formStyles.label} ${formStyles[id]}`}
            >
                {title}
                {required && showRequired ? asterisk : ''}
            </label>

            {helpText && (
                <p id={helpId} className={formStyles.help}>
                    {helpText}
                </p>
            )}

            <input
                data-testid="textinputinput"
                type={type}
                id={id}
                name={name}
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange?.(e.target.value, e)}
                onBlur={(e) => onBlur?.(e.target.value, e)}
                required={required}
                disabled={disabled}
                aria-invalid={Boolean(errorMessage) || undefined}
                aria-describedby={describedBy || undefined}
                aria-required={required || undefined}
                className={`${formStyles.input} ${rest.className ?? ''}`}
                {...rest}
            />

            {errorMessage && (
                <p id={errorId} className={formStyles.error} role="alert">
                    {errorMessage}
                </p>
            )}
        </div>
    );
};

export default TextInput;
